import React from 'react';
import { UserCheck, Plus, Edit, Trash2, Timer } from 'lucide-react';
import CustomSelect from '../../ui/CustomSelect';
import { Badge } from '../../ui/badge';
import { formatRupiah } from '../recruitment/utils/formatRupiah';

// Format hours with proper decimal formatting (e.g., 2906.10h -> 02.906.10h)
const formatHours = (hours: any): string => {
  if (!hours) return '0h';
  // Convert to number if it's a string
  const numHours = typeof hours === 'string' ? parseFloat(hours) : Number(hours);
  if (isNaN(numHours) || numHours === 0) return '0h';
  // Format with 2 decimal places
  const formatted = numHours.toFixed(2);
  const parts = formatted.split('.');
  // Add thousand separators to integer part
  const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  // Ensure at least 2 digits before first dot (pad with zero if needed)
  const paddedInteger = integerPart.length < 2 ? `0${integerPart}` : integerPart;
  return `${paddedInteger}.${parts[1]}h`;
};

const statusColors = {
  present: 'bg-green-100 text-green-700',
  late: 'bg-amber-100 text-amber-700',
  absent: 'bg-red-100 text-red-700',
  leave: 'bg-blue-100 text-blue-700',
  sick: 'bg-purple-100 text-purple-700',
  holiday: 'bg-gray-100 text-gray-700',
};

const statusLabels = {
  present: 'Hadir',
  late: 'Terlambat',
  absent: 'Tidak Hadir',
  leave: 'Cuti',
  sick: 'Sakit',
  holiday: 'Libur',
};

const filterOptions = [
  { value: 'all', label: 'Semua Status' },
  { value: 'present', label: 'Hadir' },
  { value: 'late', label: 'Terlambat' },
  { value: 'absent', label: 'Tidak Hadir' },
  { value: 'leave', label: 'Cuti' },
];

function LoadingSkeleton({ isAdmin }) {
  return Array(5)
    .fill(0)
    .map((_, i) => (
      <tr key={i} className="border-b">
        <td className="px-4 py-3">
          <div className="h-10 w-40 bg-gray-200 rounded animate-pulse"></div>
        </td>
        <td className="px-4 py-3 hidden md:table-cell">
          <div className="h-4 w-20 bg-gray-200 rounded animate-pulse"></div>
        </td>
        <td className="px-4 py-3 hidden md:table-cell">
          <div className="h-4 w-20 bg-gray-200 rounded animate-pulse"></div>
        </td>
        <td className="px-4 py-3">
          <div className="h-6 w-24 bg-gray-200 rounded animate-pulse"></div>
        </td>
        <td className="px-4 py-3">
          <div className="h-16 w-32 bg-gray-200 rounded animate-pulse"></div>
        </td>
        <td className="px-4 py-3 hidden lg:table-cell">
          <div className="h-4 w-32 bg-gray-200 rounded animate-pulse"></div>
        </td>
        {isAdmin && (
          <td className="px-4 py-3">
            <div className="h-8 w-24 bg-gray-200 rounded animate-pulse ml-auto"></div>
          </td>
        )}
      </tr>
    ));
}

function EmptyState({ isAdmin }) {
  return (
    <tr>
      <td colSpan={isAdmin ? 7 : 6} className="text-center py-12">
        <UserCheck className="w-12 h-12 mx-auto mb-3 text-gray-300" />
        <p className="text-gray-500">Tidak ada data kehadiran</p>
      </td>
    </tr>
  );
}

function AttendanceRow({
  emp,
  attendance,
  overtime,
  isAdmin,
  onEdit,
  onDelete,
  onAdd,
  onAddOvertime,
}) {
  const status = attendance?.status || 'absent';

  const overtimeStatusColors = {
    pending: 'bg-gray-900 text-white',
    approved: 'bg-green-100 text-green-700',
    rejected: 'bg-red-100 text-red-700',
  };

  const overtimeStatusLabels = {
    pending: 'Pending',
    approved: 'Disetujui',
    rejected: 'Ditolak',
  };

  return (
    <tr className="border-b hover:bg-gray-50">
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
            <span className="font-semibold text-emerald-700">{emp.full_name?.charAt(0)}</span>
          </div>
          <div className="min-w-0">
            <p className="font-medium truncate">{emp.full_name}</p>
            <p className="text-sm text-gray-500">{emp.employee_id}</p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3 hidden md:table-cell">
        {attendance?.clock_in ? <span className="font-mono">{attendance.clock_in}</span> : '-'}
      </td>
      <td className="px-4 py-3 hidden md:table-cell">
        {attendance?.clock_out ? <span className="font-mono">{attendance.clock_out}</span> : '-'}
      </td>
      <td className="px-4 py-3">
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[status]}`}>
          {statusLabels[status]}
        </span>
      </td>
      <td className="px-4 py-3">
        {overtime ? (
          <div className="space-y-2">
            <p className="text-lg font-bold text-orange-600">
              {formatHours(overtime.total_hours || 0)}
            </p>
            <p className="text-sm font-medium text-gray-900">
              Rp {formatRupiah(overtime.total_amount || 0)}
            </p>
            <Badge
              className={`${overtimeStatusColors[overtime.status] || overtimeStatusColors.pending} px-2 py-1`}
            >
              {overtimeStatusLabels[overtime.status] || 'Pending'}
            </Badge>
          </div>
        ) : (
          <span className="text-sm text-gray-400">-</span>
        )}
      </td>
      <td className="px-4 py-3 text-sm text-gray-500 hidden lg:table-cell">
        {attendance?.notes || '-'}
      </td>
      {isAdmin && (
        <td className="px-4 py-3 text-right">
          <div className="flex justify-end gap-1 items-center">
            {onAddOvertime && (
              <button
                onClick={() => onAddOvertime(emp.id)}
                className="p-2 hover:bg-orange-50 rounded-lg text-orange-600"
                title="Tambah Lembur"
              >
                <Timer className="w-4 h-4" />
              </button>
            )}
            {attendance ? (
              <>
                <button
                  onClick={() => onEdit(attendance)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={() => onDelete(attendance.id)}
                  className="p-2 hover:bg-gray-100 rounded-lg text-red-500"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => onAdd(emp.id)}
                  className="px-3 py-1 text-sm hover:bg-gray-100 rounded-lg flex items-center"
                >
                  <Plus className="w-4 h-4 mr-1" /> Input
                </button>
              </>
            )}
          </div>
        </td>
      )}
    </tr>
  );
}

export default function AttendanceTable({
  employees,
  loading,
  isAdmin,
  filterStatus,
  onFilterChange,
  selectedDate,
  getAttendanceForEmployee,
  getOvertimeForEmployee,
  onEdit,
  onDelete,
  onAdd,
  onAddOvertime,
}) {
  const formatDate = dateStr => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('id-ID', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="bg-white rounded-lg border shadow-sm">
      <div className="p-4 border-b flex flex-col md:flex-row md:items-center justify-between gap-4">
        <h3 className="font-semibold">Rekap Kehadiran - {formatDate(selectedDate)}</h3>
        <CustomSelect
          value={filterStatus}
          onChange={onFilterChange}
          options={filterOptions}
          placeholder="Semua Status"
          width="w-full md:w-44"
        />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b">
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Pegawai</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-700 hidden md:table-cell">
                Clock In
              </th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-700 hidden md:table-cell">
                Clock Out
              </th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Status</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">
                Lembur (Bulan Ini)
              </th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-700 hidden lg:table-cell">
                Catatan
              </th>
              {isAdmin && (
                <th className="text-right px-4 py-3 text-sm font-medium text-gray-700">Aksi</th>
              )}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <LoadingSkeleton isAdmin={isAdmin} />
            ) : employees.length === 0 ? (
              <EmptyState isAdmin={isAdmin} />
            ) : (
              employees.map(emp => (
                <AttendanceRow
                  key={emp.id}
                  emp={emp}
                  attendance={getAttendanceForEmployee(emp.id)}
                  overtime={getOvertimeForEmployee(emp.id)}
                  isAdmin={isAdmin}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onAdd={onAdd}
                  onAddOvertime={onAddOvertime}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
